/** @jsx jsx */
import { forwardRef } from 'react';
import { css, jsx } from '@emotion/react';
import Button, { Theme as ButtonTheme } from '@atlaskit/button/custom-theme-button';
import Heading from '@atlaskit/heading';
import { N0, N50A, N60A, P300 } from '@atlaskit/theme/colors';
import { createTheme } from '@atlaskit/theme/components';
import { layers } from '@atlaskit/theme/constants';
import { DialogActionItem, DialogActionItemContainer } from '../styled/dialog';
import { spotlightButtonTheme } from './theme';
const bodyStyles = css({
  display: 'flex',
  padding: `${"var(--ds-space-200, 16px)"} ${"var(--ds-space-250, 20px)"}`,
  flexDirection: 'column'
});
const imageStyles = css({
  display: 'block'
});
const defaultHeaderStyles = css({
  display: 'flex',
  paddingBottom: "var(--ds-space-100, 8px)",
  alignItems: 'baseline',
  justifyContent: 'space-between'
});
const DefaultHeader = ({
  children
}) => jsx("div", {
  css: defaultHeaderStyles
}, children);
const defaultFooterStyles = css({
  display: 'flex',
  paddingTop: "var(--ds-space-100, 8px)",
  alignItems: 'center',
  justifyContent: 'space-between'
});
const DefaultFooter = ({
  children
}) => jsx("div", {
  css: defaultFooterStyles
}, children);
const containerStyles = css({
  height: 'fit-content',
  zIndex: layers.spotlight() + 1,
  background: `var(--ds-background-discovery-bold, ${P300})`,
  borderRadius: "var(--ds-border-radius, 3px)",
  color: `var(--ds-text-inverse, ${N0})`,
  overflow: 'auto'
});
const containerShadowStyles = css({
  boxShadow: `var(--ds-shadow-raised, ${`0 4px 8px -2px ${N50A}, 0 0 1px ${N60A}`})`
});

/**
 * @deprecated
 */
const Theme = createTheme(() => ({
  container: {}
}));

/**
 * @deprecated
 */

/**
 * __Spotlight card__
 *
 * A spotlight card is for onboarding messages that need a more flexible layout, or don't require a dialog.
 *
 * - [Examples](https://atlassian.design/components/onboarding/spotlight-card/examples)
 * - [Code](https://atlassian.design/components/onboarding/spotlight-card/code)
 * - [Usage](https://atlassian.design/components/onboarding/spotlight-card/usage)
 */
const SpotlightCard = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    actions = [],
    actionsBeforeElement,
    children,
    components = {},
    heading,
    headingAfterElement,
    image,
    innerRef,
    isFlat,
    testId,
    theme,
    width = 400
  } = props;
  const {
    Header = DefaultHeader,
    Footer = DefaultFooter
  } = components;
  return jsx(ButtonTheme.Provider, {
    value: spotlightButtonTheme
  }, jsx(Theme.Provider, {
    value: theme
  }, jsx(Theme.Consumer, null, ({
    container
  }) => {
    return jsx("div", {
      css: [containerStyles, !isFlat && containerShadowStyles,
      // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
      container],
      style: {
        minWidth: '160px',
        maxWidth: '600px',
        width: typeof width === 'string' ? width : `${width}px`
      },
      ref: ref || innerRef,
      "data-testid": testId
    }, typeof image === 'string' ? jsx("img", {
      css: imageStyles,
      src: image,
      alt: ""
    }) : image, jsx("div", {
      css: bodyStyles
    }, heading || headingAfterElement ? jsx(Header, null, jsx(Heading, {
      color: "inverse",
      level: "h600",
      as: "h4"
    }, heading), headingAfterElement) : null, children, actions.length > 0 || actionsBeforeElement ? jsx(Footer, null, actionsBeforeElement || jsx("span", null), jsx(DialogActionItemContainer, null, actions.map(({
      text,
      key,
      ...rest
    }, idx) => {
      return jsx(DialogActionItem, {
        key: key || (typeof text === 'string' ? text : `${idx}`)
      }, jsx(Button, rest, text));
    }))) : null));
  })));
});
SpotlightCard.displayName = 'SpotlightCard';
export default SpotlightCard;